import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, Button, Alert, Spinner, Card, Row, Col, Modal, InputGroup, FormControl } from 'react-bootstrap';
import apiClient from '../../../utils/axios';
import { useAuth } from '../../auth/AuthContext';
import { groupOrder } from '../../../config/groups';

// 成員瀏覽權限管理頁面組件
const AccessManagement = () => {
  // 移除空對象模式，直接不使用 useAuth() 的返回值
  useAuth();
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // 新增分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // 當前選中的用戶和其瀏覽權限
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserAccess, setSelectedUserAccess] = useState([]);
  const [showAccessModal, setShowAccessModal] = useState(false);
  
  // 載入使用者數據
  const loadUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/admin/users`, {
        params: {
          active: true,
          search: searchTerm,
          page: page
        }
      });
      
      setUsers(response.data.users || []);
      setCurrentPage(response.data.currentPage || page);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setError('無法載入使用者數據。' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // 載入成員數據
  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await apiClient.get('/members');
      // Sort by group, then by term (ascending), then by kana (50音順), then by name
      const sortedMembers = (response.data || []).sort((a, b) => {
        // Primary sort: group (依 HOME_GROUPS 順序)
        const groupA = groupOrder(a.group);
        const groupB = groupOrder(b.group);
        if (groupA !== groupB) {
          return groupA - groupB;
        }
        // Secondary sort: term (ascending - 小到大)
        if (a.term !== b.term) {
          return a.term - b.term;
        }
        // Tertiary sort: by kana (50音順)
        if (a.kana && b.kana) {
          const kanaCompare = a.kana.localeCompare(b.kana, 'ja-JP');
          if (kanaCompare !== 0) {
            return kanaCompare;
          }
        }
        // Quaternary sort: by name (if kana is same or missing)
        return a.name.localeCompare(b.name, 'ja-JP');
      });
      setMembers(sortedMembers);
    } catch (err) {
      setError('無法載入成員數據。' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingMembers(false);
    }
  };

  // 獲取用戶的瀏覽權限
  const getUserAccess = async (userId) => {
    try {
      const response = await apiClient.get(`/admin/users/${userId}/access`);
      
      return response.data || [];
    } catch (err) {
      setError('無法獲取用戶瀏覽權限。' + (err.response?.data?.message || err.message));
      return [];
    }
  };

  // 更新用戶的瀏覽權限
  const updateUserAccess = async () => {
    try {
      await apiClient.patch(
        `/admin/users/${selectedUser.id}/access`,
        { memberIds: selectedUserAccess }
      );
      
      setShowAccessModal(false);
      setError(null);
      // 顯示成功訊息
      alert('成員瀏覽權限已更新');
    } catch (err) {
      setError('無法更新用戶瀏覽權限。' + (err.response?.data?.message || err.message));
    }
  };

  // 處理開啟瀏覽權限模態框
  const handleOpenAccessModal = async (user) => {
    setSelectedUser(user);
    const accessList = await getUserAccess(user.id);
    setSelectedUserAccess(accessList);
    setShowAccessModal(true);
  };

  // 處理勾選成員瀏覽權限
  const handleMemberAccessChange = (memberId) => {
    if (selectedUserAccess.includes(memberId)) {
      setSelectedUserAccess(selectedUserAccess.filter(id => id !== memberId));
    } else {
      setSelectedUserAccess([...selectedUserAccess, memberId]);
    }
  };

  // 全選/取消全選
  const handleSelectAll = (select) => {
    if (select) {
      setSelectedUserAccess(members.map(member => member.id));
    } else {
      setSelectedUserAccess([]);
    }
  };

  // 搜索用戶
  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers();
  };

  // 頁面變更處理函數
  const handlePageChange = (page) => {
    loadUsers(page);
  };

  // 分頁按鈕組件
  const Pagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={i === currentPage ? 'primary' : 'outline-primary'}
          className="mx-1"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }
    return (
      <div className="d-flex justify-content-center my-3">
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          上一頁
        </Button>
        {pages}
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          下一頁
        </Button>
      </div>
    );
  };

  // 初始載入
  useEffect(() => {
    loadUsers();
    loadMembers();
  }, [loadUsers]);  // 加入 loadUsers 作為依賴

  return (
    <div className="access-management p-4">
      <h3 className="mb-4">成員瀏覽權限管理</h3>

      {/* 搜索欄 */}
      <Form className="mb-4" onSubmit={handleSearch}>
        <InputGroup>
          <FormControl
            placeholder="搜索使用者名稱..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="primary" type="submit">
            搜索
          </Button>
        </InputGroup>
      </Form>

      {/* 錯誤消息 */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* 載入狀態 */}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-3">正在載入用戶數據...</p>
        </div>
      ) : (
        <>
          {/* 桌面版表格 - 在平板和大螢幕上顯示 */}
          <div className="d-none d-md-block table-responsive">
            <Table hover className="align-middle border">
              <thead className="bg-light">
                <tr>
                  <th>使用者名稱</th>
                  <th>電子郵件</th>
                  <th>權限設定</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center">沒有找到使用者</td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleOpenAccessModal(user)}
                        >
                          設定瀏覽權限
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* 行動裝置版卡片 - 只在小螢幕上顯示 */}
          <div className="d-md-none">
            {users.length === 0 ? (
              <Card className="text-center p-3">
                <p>沒有找到使用者</p>
              </Card>
            ) : (
              users.map((user) => (
                <Card key={user.id} className="mb-3">
                  <Card.Body>
                    <Card.Title>{user.username}</Card.Title>
                    <Card.Subtitle className="mb-3 text-muted">{user.email}</Card.Subtitle>
                    <div className="d-grid">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleOpenAccessModal(user)}
                      >
                        設定瀏覽權限
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))
            )}
          </div>

          {/* 分頁按鈕 */}
          <Pagination />
        </>
      )}

      {/* 瀏覽權限設定模態框 */}
      <Modal
        show={showAccessModal}
        onHide={() => setShowAccessModal(false)}
        size="lg"
        aria-labelledby="access-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="access-modal">
            設定用戶瀏覽權限 - {selectedUser?.username}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingMembers ? (
            <div className="text-center my-3">
              <Spinner animation="border" role="status" variant="primary" />
              <p className="mt-2">正在載入成員數據...</p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between mb-3">
                <Button variant="outline-primary" onClick={() => handleSelectAll(true)}>
                  全選
                </Button>
                <Button variant="outline-secondary" onClick={() => handleSelectAll(false)}>
                  取消全選
                </Button>
              </div>

              <Row>
                {members.map((member) => (
                  <Col md={6} lg={4} key={member.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`member-${member.id}`}
                      label={member.name}
                      checked={selectedUserAccess.includes(member.id)}
                      onChange={() => handleMemberAccessChange(member.id)}
                    />
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAccessModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={updateUserAccess} disabled={loadingMembers}>
            保存變更
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AccessManagement;